import { useState, useRef, useEffect } from "react";
import {
  UploadCloud,
  FileText,
  X,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Camera,
  Image as ImageIcon,
  FolderOpen,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  uploadPolicy,
  isSupportedFile,
  MAX_FILE_BYTES,
  downloadUrl,
  recordUploadConsentServer,
} from "../lib/uploads";
import { hasUploadConsent, recordUploadConsent } from "../lib/consent";

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const UploadModal = ({ open, onClose, onUploaded }) => {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [consented, setConsented] = useState(() => hasUploadConsent());
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setFile(null);
      setStage("idle");
      setDragOver(false);
      setProgress(0);
      setError("");
      setResult(null);
      // Re-read consent in case it was recorded during this session.
      setConsented(hasUploadConsent());
    }
  }, [open]);

  const acceptConsent = () => {
    recordUploadConsent();
    setConsented(true);
    // Fire-and-forget server-side echo for audit trail. Don't block the UI
    // if the network is flaky — localStorage is still the source of truth
    // for gating uploads on this device.
    recordUploadConsentServer({ version: "v1" }).catch(() => {});
  };

  const parseServerError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 413) return "File exceeds 20 MB limit.";
    if (status === 415) return detail || "Only PDF files are accepted.";
    if (status === 429)
      return detail || "Too many uploads. Please try again later.";
    if (status === 400) return detail || "Bad request.";
    if (status === 502) return "Storage error. Please retry.";
    if (err?.code === "ERR_NETWORK")
      return "Network error — check your connection.";
    return detail || "Upload failed. Please retry.";
  };

  const handleFiles = async (files) => {
    const f = files?.[0];
    if (!f) return;

    // client-side validation
    if (!isSupportedFile(f)) {
      setFile(f);
      setStage("error");
      setError("Please upload a PDF or a photo (JPG, PNG or HEIC).");
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFile(f);
      setStage("error");
      setError(`File is ${humanSize(f.size)} — max allowed is 20 MB.`);
      return;
    }

    setFile(f);
    setStage("uploading");
    setProgress(0);
    setError("");
    try {
      const data = await uploadPolicy(f, { onProgress: setProgress });
      setResult(data);
      setStage("done");
      onUploaded?.(data);
    } catch (err) {
      setStage("error");
      setError(parseServerError(err));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const retry = () => {
    setStage("idle");
    setError("");
    setProgress(0);
    setFile(null);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      data-testid="upload-modal-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="upload-modal"
      >
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Upload your policy
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              PDF or a photo · Any insurer · Max 20 MB
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="upload-modal-close"
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close upload modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          {!consented && stage === "idle" ? (
            <div
              className="border border-amber-200 bg-amber-50/60 rounded-2xl p-5"
              data-testid="upload-consent-gate"
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-display font-semibold text-slate-900">
                    Before you upload
                  </h4>
                  <p className="mt-1 text-sm text-slate-700 leading-relaxed">
                    We&apos;ll extract text from your policy and generate a
                    plain-English explanation. Please read and accept the terms
                    once to continue.
                  </p>
                </div>
              </div>

              <label
                className="mt-4 flex items-start gap-3 cursor-pointer"
                data-testid="consent-label"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  data-testid="consent-checkbox"
                  onChange={(e) => {
                    if (e.target.checked) acceptConsent();
                    else setConsented(false);
                  }}
                  checked={consented}
                />
                <span className="text-sm text-slate-800 leading-relaxed">
                  I understand this is an automated explanation, not insurance
                  advice, and I&apos;ll verify with my insurer before acting.
                </span>
              </label>

              <p className="mt-3 text-xs text-slate-500">
                Full{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2"
                  data-testid="consent-terms-link"
                >
                  Terms &amp; Disclaimer
                </Link>{" "}
                open in a new tab.
              </p>
            </div>
          ) : null}

          {consented && stage === "idle" && (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
                className={`border-2 border-dashed rounded-2xl p-8 sm:p-10 text-center cursor-pointer ${
                  dragOver
                    ? "border-blue-500 bg-blue-50/50"
                    : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                }`}
              >
                <div className="mx-auto h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <UploadCloud className="h-6 w-6" />
                </div>
                <p className="mt-4 font-display font-semibold text-slate-900">
                  Upload PDF or photo
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  PDF, JPG, PNG or HEIC · Up to 20 MB
                </p>
                <p className="text-xs text-slate-400 mt-1 hidden sm:block">
                  Or drag & drop anywhere in this box
                </p>
              </div>

              {/* Explicit mobile-first choices — Files, Gallery, Camera */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="upload-source-buttons">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-choose-file"
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 px-2 py-3 text-xs font-medium text-slate-700 min-h-[80px] justify-center"
                >
                  <FolderOpen className="h-5 w-5 text-blue-600" />
                  Files (PDF)
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-choose-gallery"
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 px-2 py-3 text-xs font-medium text-slate-700 min-h-[80px] justify-center"
                >
                  <ImageIcon className="h-5 w-5 text-blue-600" />
                  Photo Gallery
                </button>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="upload-choose-camera"
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 px-2 py-3 text-xs font-medium text-slate-700 min-h-[80px] justify-center"
                >
                  <Camera className="h-5 w-5 text-blue-600" />
                  Take Photo
                </button>
              </div>

              {/* Files & Gallery share this input. Mobile OSs show a picker
                  offering Files, Gallery AND Camera because we accept both
                  PDF + image/* without a `capture` attribute. */}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.heic,.heif"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="upload-file-input"
              />
              {/* Camera-only input: `capture` hints the OS to jump straight
                  to the rear camera instead of the file picker. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="upload-camera-input"
              />
            </>
          )}

          {stage === "uploading" && (
            <div
              className="border border-slate-200 rounded-2xl p-5 bg-slate-50"
              data-testid="upload-progress"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">
                    {file?.name || "policy.pdf"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {file ? humanSize(file.size) : ""} · Uploading…
                  </p>
                </div>
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin shrink-0" />
              </div>
              <div className="mt-4">
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600"
                    style={{
                      width: `${progress}%`,
                      transition: "width 200ms linear",
                    }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                  <span>{progress}%</span>
                  <span>Encrypted in transit</span>
                </div>
              </div>
            </div>
          )}

          {stage === "done" && (
            <div
              className="border border-emerald-200 bg-emerald-50 rounded-2xl p-5"
              data-testid="upload-success"
            >
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-emerald-900">
                    Uploaded successfully
                  </p>
                  <p className="text-sm text-emerald-700/90 mt-0.5 truncate">
                    {result?.filename} · {humanSize(result?.size || 0)}
                  </p>
                  {result && (
                    <a
                      href={downloadUrl(result.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="upload-view-pdf"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 hover:text-emerald-900"
                    >
                      Open uploaded PDF
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {stage === "error" && (
            <div
              className="border border-red-200 bg-red-50 rounded-2xl p-5 flex items-start gap-4"
              data-testid="upload-error"
            >
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-red-900">Upload failed</p>
                <p className="text-sm text-red-700 mt-0.5" data-testid="upload-error-msg">
                  {error}
                </p>
                <button
                  onClick={retry}
                  data-testid="upload-retry"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-4"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500 max-w-[60%]">
              We explain your policy — never advise. IRDAI-safe.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                data-testid="upload-cancel"
                className="text-slate-600 hover:text-slate-900 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-100"
              >
                {stage === "done" ? "Close" : "Cancel"}
              </button>
              <button
                disabled={stage !== "done"}
                onClick={() => {
                  onClose?.();
                  navigate("/dashboard");
                }}
                data-testid="upload-goto-dashboard"
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-full px-5 py-2.5 shadow-sm"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
